import { onScopeDispose, shallowRef, watchEffect } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { hasUnsavedChanges } from "@/services/unsavedChanges"
import { DEPLOYMENT_KIND } from "@/services/deployment"

// The browser tab names the view it shows, so a row of Lewara tabs can be told apart without opening
// each one. Two sources, most specific first:
//
// - the record a detail view has loaded ("Kupferrohr"), set by that view through `useDocumentTitle`;
// - the route's `meta.title`, an i18n key ("nav.material.overview"), for every route.
//
// Joined as "Kupferrohr · Materialien · Lewara". The record goes first because a narrow tab shows only
// the start of its title, and the record's name is what tells two open detail tabs apart.
//
// Only a test deployment's short name comes before it ("Vorab · Kupferrohr · …", services/deployment.ts):
// a test tab beside a production one must never be mistaken for it, whichever record each shows.
//
// A tab with edits not saved yet starts with "• " (`hasUnsavedChanges`), the mark editors put on an
// unsaved file, so the tab left open over lunch says so from the tab strip.

const APP_NAME = "Lewara"
const SEPARATOR = " · "
const UNSAVED_MARK = "• "

/**
 * The record name of the view currently on screen, tagged with the view that set it.
 *
 * Tagged so that clearing it on unmount cannot wipe a name the *next* view already set: during a
 * route change the incoming view may mount before the outgoing one has finished unmounting.
 */
const record = shallowRef<{ owner: symbol; name: string } | null>(null)

/**
 * Puts the loaded record's name in front of the tab title, for as long as the calling view is mounted.
 *
 * @param getName read reactively — pass the same expression the view's heading shows, so the tab
 *   follows a rename and stays on the route title while the record is still loading (empty name).
 */
export function useDocumentTitle(getName: () => string | null | undefined) {
  const owner = Symbol("documentTitle")

  watchEffect(() => {
    const name = getName()?.trim()
    if (name) record.value = { owner, name }
    else if (record.value?.owner === owner) record.value = null
  })

  onScopeDispose(() => {
    if (record.value?.owner === owner) record.value = null
  })
}

/**
 * Keeps `document.title` in step with the route, the loaded record and the locale. Called once, from
 * the root component — it is the only one that lives across every navigation.
 */
export function useDocumentTitleSync() {
  const route = useRoute()
  const { t } = useI18n()

  const deploymentKey = DEPLOYMENT_KIND && `general.deployment.short.${DEPLOYMENT_KIND}`

  watchEffect(() => {
    const titleKey = route.meta.title as string | undefined
    const title = [deploymentKey && t(deploymentKey), record.value?.name, titleKey && t(titleKey), APP_NAME]
      .filter(Boolean)
      .join(SEPARATOR)
    document.title = hasUnsavedChanges.value ? `${UNSAVED_MARK}${title}` : title
  })
}
